import { ReactNode, useCallback, useMemo, useState } from "react"

import { TextFilterT, FilterType, OptionT } from "./types/filter.types"
import { UseFilterProps, UseFilterRes } from "./types/useFilter.types"
import { useSortFilterState } from "../../context/SortFilterProvider"

export function optionFromValue<TValue>(
  value: TValue,
  keyExtractor: (value: TValue) => string,
  labelExtractor: (value: TValue) => string,
  contentExtractor?: (value: TValue) => ReactNode
): OptionT {
  return {
    key: keyExtractor(value),
    value: labelExtractor(value),
    content: contentExtractor?.(value),
  }
}

interface BaseFilterProps<TValue> extends UseFilterProps<TValue> {
  selectedKeys: string[]
  setSelectedKeys: (keys: string[]) => void
}

export function useBaseFilter<TValue>({
  key,
  label,
  values,
  sort,
  keyExtractor,
  labelExtractor,
  contentExtractor,
  onChangeQuery,
  autoFilterOptions = true,
  selectedKeys,
  setSelectedKeys,
  allowExternalValues = false,
  initialSelectedOptions = [],
  onLoadMore,
}: BaseFilterProps<TValue>): UseFilterRes<TextFilterT> {
  const [internalSelectedOptions, setInternalSelectedOptions] = useState<
    OptionT[]
  >(initialSelectedOptions)

  const valuesByKey = useMemo(
    () =>
      values.reduce<Record<string, TValue>>((acc, value) => {
        acc[keyExtractor(value)] = value
        return acc
      }, {}),
    [values, keyExtractor]
  )

  const selectedOptions = useMemo(() => {
    const sourceOptions = allowExternalValues
      ? internalSelectedOptions
      : selectedKeys.map((key) => ({ key, value: key }))

    return sourceOptions.map((option) => {
      const value = valuesByKey[option.key]
      return value
        ? optionFromValue(value, keyExtractor, labelExtractor, contentExtractor)
        : option
    })
  }, [
    allowExternalValues,
    internalSelectedOptions,
    selectedKeys,
    valuesByKey,
    keyExtractor,
    labelExtractor,
    contentExtractor,
  ])

  const selectedOptionKeys = useMemo(
    () => selectedOptions.map((o) => o.key),
    [selectedOptions]
  )

  const options = useMemo(() => {
    const sorted = sort ? [...values].sort(sort) : values
    return sorted.map((value) =>
      optionFromValue(value, keyExtractor, labelExtractor, contentExtractor)
    )
  }, [values, keyExtractor, labelExtractor, contentExtractor, sort])

  const onSelectOption = useCallback(
    (option: OptionT) => {
      const currentOptions = allowExternalValues
        ? internalSelectedOptions
        : selectedKeys.map((key) => ({ key, value: key }))

      const isSelected = selectedOptionKeys.includes(option.key)

      const newOptions = isSelected
        ? currentOptions.filter((opt) => opt.key !== option.key)
        : [...currentOptions, option]

      allowExternalValues && setInternalSelectedOptions(newOptions)
      setSelectedKeys(newOptions.map((opt) => opt.key))
    },
    [
      allowExternalValues,
      internalSelectedOptions,
      selectedKeys,
      selectedOptionKeys,
      setSelectedKeys,
    ]
  )

  const onClear = useCallback(() => {
    setSelectedKeys([])
    allowExternalValues && setInternalSelectedOptions([])
  }, [setSelectedKeys, allowExternalValues])

  const filter = {
    type: FilterType.Text,
    key,
    value: label,
    options,
    selectedOptions,
    onSelectOption,
    onClear,
    onChangeQuery,
    autoFilterOptions,
    onLoadMore,
  } satisfies TextFilterT

  return { filter, selectedKeys: selectedOptionKeys }
}

export function useLocalFilter<TValue>(
  props: UseFilterProps<TValue>
): UseFilterRes<TextFilterT> {
  const [selectedKeys, setSelectedKeys] = useState<string[]>(
    (props.initialSelectedOptions ?? []).map((option) => option.key)
  )

  return useBaseFilter({ ...props, selectedKeys, setSelectedKeys })
}

export function useContextFilters<TValue>({
  key,
  label,
  values,
  sort,
  keyExtractor,
  labelExtractor,
  contentExtractor,
  onChangeQuery,
  allowExternalValues = false,
  autoFilterOptions = true,
  onLoadMore,
}: UseFilterProps<TValue>): UseFilterRes<TextFilterT> {
  const { filters, setFilters } = useSortFilterState()

  const selectedKeys = filters[key] ?? []
  const setSelectedKeys = useCallback(
    (newFilters: string[]) => setFilters(key, newFilters),
    [setFilters, key]
  )

  return useBaseFilter({
    key,
    label,
    values,
    sort,
    keyExtractor,
    labelExtractor,
    contentExtractor,
    onChangeQuery,
    autoFilterOptions,
    selectedKeys,
    setSelectedKeys,
    allowExternalValues,
    onLoadMore,
  })
}
